import React, { useEffect, useState } from "react";
import {
  BookOpen, Briefcase, CalendarClock, ClipboardList, FileText, GraduationCap, IndianRupee, Layers,
  Plus, Save, Trash2, User, UserCog, UserPlus,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, FormActionBar, FormField, IconButton, Input,
  NativeSelect, PageHeader, PhoneInput, ProgressBar, Radio, ResetButton, Textarea, cn,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import FeePlanModal from "../Fees/FeePlanModal";
import CourseFeeFields from "./CourseFeeFields";
import EducationFields from "./profile/EducationFields";
import WorkHistoryEditor from "./profile/WorkHistoryEditor";
import DocumentsPanel from "./profile/DocumentsPanel";
import useProfileOptions from "./profile/useProfileOptions";
import { experienceMonths } from "./profile/studentProfileRules";
import apiClient from "../../utils/apiClient";
import { EMPTY_FEE, feeFromCourse, feePayload, priceFeeState } from "../../utils/enrolmentFee";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../contexts/BranchContext";

const EMPTY_ADDITIONAL = [];
const NO_FEE_ERROR = null;
const NO_DOCS = null;

const initialForm = {
  name: '',
  email: '',
  mobile: '',
  dob: '',
  gender: '',
  address: '',
  guardianName: '',
  guardianMobile: '',
  guardianEmail: '',
  course: '',
  batch: '',
  degree: '',
  degreeBranch: '',
  college: '',
  percentage: '',
  passout: '',
  experience: '',
  workHistory: [],
  totalFees: '',
  paidFees: '0',
  pendingFees: '',
  placement: 'Yes',
  refName: '',
  refNumber: '',
  comments: '',
  status: 'active',
  joinedDate: '',
};

// Coerce a fee input (string/number/undefined) to a finite number for math.
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

/**
 * Additional-course rows carry their own fee editor state (`fee`) and, when
 * the enrolment is already saved, what it was saved as (`saved`: courseId,
 * fee, totalFees, paidFees). `saved` is what lets a row switched to another
 * course and back get its own fee and discount again, and what the paid-amount
 * rule is checked against — but only while the row still names that course.
 */
const storedOf = (row) => (
  row.saved && row.courseId && row.courseId === row.saved.courseId
    ? { totalFees: row.saved.totalFees, paidFees: row.saved.paidFees }
    : null
);

/** A card on the form: an icon, a title, its fields. */
const Section = ({ icon: Icon, title, children, action }) => (
  <AppCard>
    <AppCardHeader className={cn(action && "tw-flex-row tw-items-center tw-justify-between tw-gap-3")}>
      <AppCardTitle className="tw-flex tw-items-center tw-gap-2">
        <Icon size={16} aria-hidden="true" /> {title}
      </AppCardTitle>
      {action}
    </AppCardHeader>
    <AppCardContent>{children}</AppCardContent>
  </AppCard>
);

/** The two-column field grid every section lays its fields in. */
const Grid = ({ children }) => <div className="tw-grid tw-gap-4 min-[576px]:tw-grid-cols-2">{children}</div>;
const FULL = "min-[576px]:tw-col-span-2";

/** A titled block inside a card, below the first one. */
const Subsection = ({ icon: Icon, title, aside, children }) => (
  <div className="tw-mt-4 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-pt-4">
    <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
      <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-content">
        <Icon size={15} aria-hidden="true" /> {title}
      </span>
      {aside}
    </div>
    {children}
  </div>
);

const EmptyText = ({ children }) => <p className="tw-m-0 tw-py-2 tw-text-center tw-text-sm tw-text-content-subtle">{children}</p>;

const FeeTile = ({ label, value, tone }) => (
  <div className={cn(
    "tw-flex tw-flex-col tw-gap-1 tw-rounded-sm tw-border tw-border-solid tw-p-3",
    tone === "pending" ? "tw-border-warning/40 tw-bg-warning/[0.06]" : "tw-border-line tw-bg-surface-sunken",
  )}>
    <span className="tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">{label}</span>
    <span className={cn(
      "tw-text-base tw-font-medium tw-tabular-nums",
      tone === "paid" ? "tw-text-success-fg" : tone === "pending" ? "tw-text-warning-fg" : "tw-text-content",
    )}>
      {value}
    </span>
  </div>
);

/**
 * StudentForm — Add / Edit student.
 *
 * Props beyond the data:
 *   advancedProfile — the "advanced student profile" module is on: previous
 *                     companies under Academic details (when there is
 *                     experience) and the Documents card
 *   docs            — edit mode: useStudentDocuments(staffDocumentsApi(id)),
 *                     so a file is stored the moment it is chosen. Add mode has
 *                     no record yet: files are QUEUED and handed to `onSubmit`
 *                     as its third argument, to upload once the student exists.
 */
const StudentForm = ({
  initialData = initialForm,
  // Stable defaults: an effect below depends on batchOptions, and a fresh `[]`
  // on every render re-ran it on every render.
  courseOptions = EMPTY_ADDITIONAL,
  batchOptions = EMPTY_ADDITIONAL,
  loading = false,
  toast,
  setToast,
  mode = "add",
  onSubmit,
  onCancel,
  onChangeCourse,
  initialAdditionalCourses = EMPTY_ADDITIONAL,
  courseAssignments = EMPTY_ADDITIONAL,
  // The primary course's fee as saved (edit), and its { totalFees, paidFees }.
  initialPrimaryFee = EMPTY_FEE,
  primaryStored = null,
  // The save's refusal for one course: { courseId, message }.
  feeError = NO_FEE_ERROR,
  advancedProfile = false,
  docs = NO_DOCS,
  // Add mode, after the save: the queued documents going up — { done, total, percent, name }.
  uploading = null,
}) => {
  const [form, setForm] = useState({ ...initialForm, ...initialData });
  const [primaryFee, setPrimaryFee] = useState(initialPrimaryFee);
  const [additionalCourses, setAdditionalCourses] = useState(initialAdditionalCourses);
  const [additionalBatchOptions, setAdditionalBatchOptions] = useState({});
  const [filteredBatchOptions, setFilteredBatchOptions] = useState(batchOptions);
  const [selectedBranch, setSelectedBranch] = useState(
    () => (initialData?.branchId?._id || initialData?.branchId || '')
  );
  // Documents chosen before the student exists (add mode).
  const [pendingDocs, setPendingDocs] = useState([]);
  // ── Installment (EMI) plans — edit mode only, keyed by courseAssignmentId ──
  const [feePlans, setFeePlans] = useState([]);
  const [planModalCA, setPlanModalCA] = useState(null);
  const navigate = useNavigate();
  const { branches, isMultiBranch } = useBranch();
  const { options } = useProfileOptions();

  const studentId = initialData?._id;

  const loadFeePlans = React.useCallback(() => {
    if (mode !== "edit" || !studentId) return;
    apiClient
      .get(`/fees/plans/student/${studentId}`)
      .then(({ data }) => setFeePlans(data.plans || []))
      .catch(() => setFeePlans([]));
  }, [mode, studentId]);

  useEffect(() => { loadFeePlans(); }, [loadFeePlans]);

  const planForCA = (caId) =>
    feePlans.find((p) => String(p.courseAssignmentId?._id || p.courseAssignmentId) === String(caId)) || null;

  useEffect(() => {
    setForm({ ...initialForm, ...initialData });
  }, [initialData]);

  useEffect(() => {
    setPrimaryFee(initialPrimaryFee);
  }, [initialPrimaryFee]);

  const courseLabel = (courseId) => courseOptions.find((c) => c.value === courseId)?.label || "This course";
  // The course's own fee: a number, null when it names none, undefined when unknown.
  const listFeeOf = (courseId) => courseOptions.find((c) => c.value === courseId)?.fee;

  // The saved primary enrolment only describes the course it was saved for.
  const primaryStoredNow = form.course && form.course === initialData.course ? primaryStored : null;

  // Keep filteredBatchOptions in sync with parent batchOptions when no branch selected
  useEffect(() => {
    if (!selectedBranch) {
      setFilteredBatchOptions(batchOptions);
    }
  }, [batchOptions, selectedBranch]);

  useEffect(() => {
    setAdditionalCourses(initialAdditionalCourses);
    initialAdditionalCourses.forEach((ac, idx) => {
      if (ac.courseId) fetchBatchesForAdditional(idx, ac.courseId);
    });
  }, [initialAdditionalCourses]);

  const fetchBatchesForAdditional = async (index, courseId) => {
    if (!courseId) {
      setAdditionalBatchOptions(prev => ({ ...prev, [index]: [] }));
      return;
    }
    try {
      const { data } = await apiClient.get("/batches/list", { params: { course: courseId } });
      setAdditionalBatchOptions(prev => ({
        ...prev,
        [index]: (data.batches || []).map(b => ({ value: b._id, label: b.name })),
      }));
    } catch {
      setAdditionalBatchOptions(prev => ({ ...prev, [index]: [] }));
    }
  };

  // Fetch batches filtered by branch for primary course
  const fetchBranchBatches = async (courseId, branchId) => {
    if (!courseId) { setFilteredBatchOptions([]); return; }
    try {
      const headers = branchId ? { "X-Branch-Id": branchId } : {};
      const { data } = await apiClient.get("/batches/list", { params: { course: courseId }, headers });
      setFilteredBatchOptions((data.batches || []).map(b => ({ value: b._id, label: b.name })));
    } catch {
      setFilteredBatchOptions([]);
    }
  };

  // This control used to be a batch FILTER only — it narrowed the dropdown and
  // was then thrown away, because Student had no branch field to store. It now
  // also sets the student's home branch: the branch that owns them, counts them
  // in its headcount, and keeps them out of every other branch's list.
  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
    setForm(prev => ({ ...prev, batch: '', branchId: branchId || null }));
    if (form.course) {
      fetchBranchBatches(form.course, branchId);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedForm = { ...form, [name]: value };
    if (name === "course") {
      updatedForm.batch = "";
      // Picking a course prices it at the course's own fee with no discount.
      // Going back to the course this student is saved on restores what was
      // saved instead — re-picking it must not silently drop their discount.
      setPrimaryFee(
        value && value === initialData.course ? initialPrimaryFee : feeFromCourse(value ? listFeeOf(value) : "")
      );
      if (onChangeCourse) onChangeCourse(value);
      if (selectedBranch) {
        fetchBranchBatches(value, selectedBranch);
      }
    }
    setForm(updatedForm);
  };

  // PhoneInput hands back the number in its stored shape, not an event.
  const setPhone = (name) => (value) => setForm((prev) => ({ ...prev, [name]: value }));
  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }));

  // Every course priced by utils/enrolmentFee — the same rules the server
  // re-applies on save, so what the overview shows is what gets stored.
  const primaryPlan = form.course ? priceFeeState(primaryFee, primaryStoredNow, courseLabel(form.course)) : null;
  const additionalPlans = additionalCourses.map((ac) => (
    ac.courseId ? priceFeeState(ac.fee || EMPTY_FEE, storedOf(ac), courseLabel(ac.courseId)) : null
  ));

  /** The first reason the save would be refused, worded as the server words it. */
  const feeProblem = () => {
    const seen = new Set(form.course ? [form.course] : []);
    for (const ac of additionalCourses) {
      if (!ac.courseId) continue;
      if (seen.has(ac.courseId)) {
        return ac.courseId === form.course
          ? `${courseLabel(ac.courseId)} is the primary course and is also listed under additional courses. Remove one.`
          : `${courseLabel(ac.courseId)} is listed twice under additional courses. Remove one.`;
      }
      seen.add(ac.courseId);
    }
    return [primaryPlan, ...additionalPlans].find((p) => p?.error)?.error || null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const problem = feeProblem();
    if (problem) {
      setToast({ show: true, message: problem, type: "error" });
      return;
    }
    // `totalFees` stays the payable (net of the discount) for every reader of
    // the student; `primaryFee` is how it was reached, which the server prices
    // again. With no primary course, the student's own figure is left as it was.
    const payload = form.course
      ? { ...form, totalFees: primaryPlan.set.totalFees, primaryFee: feePayload(primaryFee) }
      : form;
    onSubmit(
      payload,
      additionalCourses.map((ac, idx) => ({
        courseId: ac.courseId,
        batchId: ac.batchId,
        ...(ac.courseId ? { totalFees: additionalPlans[idx].set.totalFees, ...feePayload(ac.fee || EMPTY_FEE) } : {}),
      })),
      pendingDocs,
    );
  };

  const handleReset = () => {
    setForm({ ...initialForm, ...initialData });
    setPrimaryFee(initialPrimaryFee);
    setAdditionalCourses(initialAdditionalCourses);
    setAdditionalBatchOptions({});
    setPendingDocs([]);
  };

  const addAdditionalCourse = () => {
    setAdditionalCourses(prev => [...prev, { courseId: '', batchId: '', fee: EMPTY_FEE, saved: null }]);
  };

  const removeAdditionalCourse = (index) => {
    setAdditionalCourses(prev => prev.filter((_, i) => i !== index));
    // Batch lists are keyed by row index, so the rows below move up one.
    setAdditionalBatchOptions(prev => {
      const updated = {};
      Object.entries(prev).forEach(([key, options]) => {
        const i = Number(key);
        if (i < index) updated[i] = options;
        else if (i > index) updated[i - 1] = options;
      });
      return updated;
    });
  };

  const updateAdditionalCourse = (index, field, value) => {
    setAdditionalCourses(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'courseId') {
        const row = updated[index];
        row.batchId = '';
        // Priced at the course's own fee, or — back on the course this row was
        // saved with — at what was saved, discount and all.
        row.fee = row.saved && value === row.saved.courseId
          ? row.saved.fee
          : feeFromCourse(value ? listFeeOf(value) : '');
        fetchBatchesForAdditional(index, value);
      }
      return updated;
    });
  };

  const updateAdditionalFee = (index, fee) => {
    setAdditionalCourses(prev => prev.map((ac, i) => (i === index ? { ...ac, fee } : ac)));
  };

  // A course another row already holds is not offered again; the row's own
  // course always stays in its list, so a select never loses its value.
  const additionalOptionsFor = (idx) => courseOptions.filter((opt) => (
    opt.value === additionalCourses[idx].courseId
    || (opt.value !== form.course && !additionalCourses.some((o, j) => j !== idx && o.courseId === opt.value))
  ));

  // ── Fees overview (aggregate across primary + additional courses) ──
  const payableOf = (plan) => plan?.set?.totalFees || 0;
  const discountOf = (plan) => plan?.set?.discount?.amount || 0;
  const feesTotal = payableOf(primaryPlan) + additionalPlans.reduce((s, p) => s + payableOf(p), 0);
  const feesDiscount = discountOf(primaryPlan) + additionalPlans.reduce((s, p) => s + discountOf(p), 0);
  const feesPaid = num(primaryStoredNow?.paidFees)
    + additionalCourses.reduce((s, ac) => s + num(storedOf(ac)?.paidFees), 0);
  const feesPending = Math.max(feesTotal - feesPaid, 0);
  const courseCount = (form.course ? 1 : 0) + additionalCourses.filter(ac => ac.courseId).length;

  // The payable each saved enrolment will have after this save — for telling
  // the admin when an EMI plan no longer adds up to it.
  const payableByCourse = new Map();
  if (form.course && primaryPlan?.set) payableByCourse.set(form.course, primaryPlan.set.totalFees);
  additionalCourses.forEach((ac, idx) => {
    if (ac.courseId && additionalPlans[idx]?.set) payableByCourse.set(ac.courseId, additionalPlans[idx].set.totalFees);
  });
  const serverErrorFor = (courseId) => (feeError && feeError.courseId === courseId ? feeError.message : "");

  // ── Advanced profile: previous companies and documents ──
  const months = experienceMonths(form.experience);
  const workHistory = Array.isArray(form.workHistory) ? form.workHistory : [];
  const showWork = advancedProfile && months > 0;
  const docSections = options.documentSections || [];
  const liveDocs = mode === "edit" && docs;

  return (
    <form className="tw-flex tw-flex-col tw-gap-6 tw-pb-20" onSubmit={handleSubmit}>
      <PageHeader
        icon={mode === "edit" ? UserCog : UserPlus}
        title={mode === "edit" ? "Edit Student" : "Add New Student"}
        description={mode === "edit" ? `Editing ${form.name || "student"}` : "Register a new student in your institute"}
        backButton
        onBack={onCancel || (() => navigate("/students"))}
      />

      <div className="tw-grid tw-items-start tw-gap-6 min-[992px]:tw-grid-cols-2">
        {/* ═══ Left column ═══ */}
        <div className="tw-flex tw-flex-col tw-gap-6">
          <Section icon={User} title="Personal Details">
            <Grid>
              <FormField label="Full Name" required>
                <Input name="name" value={form.name} onChange={handleChange} placeholder="Enter full name" />
              </FormField>
              <FormField label="Mobile Number" htmlFor="sf-mobile" required>
                <PhoneInput id="sf-mobile" name="mobile" value={form.mobile} onChange={setPhone("mobile")} placeholder="10-digit number" countryLabel="Mobile number country code" required />
              </FormField>
              <FormField label="Email" required>
                <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="student@email.com" />
              </FormField>
              <FormField label="Date of Birth">
                <Input type="date" name="dob" value={form.dob ? form.dob.slice(0, 10) : ""} onChange={handleChange} />
              </FormField>
              <FormField label="Gender">
                <NativeSelect name="gender" value={form.gender} onChange={handleChange}>
                  <option value="">Select gender</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </NativeSelect>
              </FormField>
              <FormField label="Address">
                <Input name="address" value={form.address} onChange={handleChange} placeholder="City, State" />
              </FormField>
              <FormField label="Guardian Name">
                <Input name="guardianName" value={form.guardianName || ""} onChange={handleChange} placeholder="Parent / guardian name" />
              </FormField>
              <FormField label="Guardian Mobile" htmlFor="sf-guardian-mobile">
                <PhoneInput id="sf-guardian-mobile" name="guardianMobile" value={form.guardianMobile || ""} onChange={setPhone("guardianMobile")} placeholder="For absence alerts" countryLabel="Guardian mobile country code" />
              </FormField>
              <FormField label="Guardian Email">
                <Input name="guardianEmail" type="email" value={form.guardianEmail || ""} onChange={handleChange} placeholder="For absence alerts" />
              </FormField>
            </Grid>
          </Section>

          <Section icon={GraduationCap} title="Academic Details">
            <Grid>
              <EducationFields
                value={form}
                onChange={patch}
                degrees={options.degrees}
              />
              <FormField label="Passed Out Year">
                <Input name="passout" value={form.passout} onChange={handleChange} placeholder="e.g. 2024" />
              </FormField>
              <FormField label="Experience" hint="Years, or e.g. 6 months — a fresher leaves it blank">
                <Input name="experience" value={form.experience} onChange={handleChange} placeholder="e.g. 2 years" />
              </FormField>
              <FormField label="Joined Date">
                <Input type="date" name="joinedDate" value={form.joinedDate ? form.joinedDate.slice(0, 10) : ""} onChange={handleChange} />
              </FormField>
            </Grid>

            {showWork && (
              <Subsection icon={Briefcase} title="Previous companies">
                <WorkHistoryEditor rows={workHistory} onChange={(rows) => patch({ workHistory: rows })} />
              </Subsection>
            )}
          </Section>

          {advancedProfile && docSections.length > 0 && (
            <Section icon={FileText} title="Documents">
              {mode === "add" && (
                <p className="tw-mb-3 tw-mt-0 tw-text-xs tw-text-content-subtle">
                  Files are uploaded when the student is saved.
                </p>
              )}
              <DocumentsPanel
                sections={docSections}
                documents={liveDocs ? docs.documents : []}
                pending={mode === "add" ? pendingDocs : []}
                experienceMonths={months}
                workHistory={workHistory}
                accept={options.documentAccept}
                imageAccept={options.imageAccept}
                maxMb={options.documentMaxMb}
                busy={liveDocs ? docs.busy : false}
                onUpload={liveDocs ? docs.upload : undefined}
                onRemove={liveDocs ? docs.remove : undefined}
                onQueue={mode === "add" ? (item) => setPendingDocs((p) => [...p, item]) : undefined}
                onUnqueue={mode === "add" ? (id) => setPendingDocs((p) => p.filter((d) => d.id !== id)) : undefined}
              />
            </Section>
          )}
        </div>

        {/* ═══ Right column ═══ */}
        <div className="tw-flex tw-flex-col tw-gap-6">
          <Section icon={BookOpen} title="Courses & Fees">
            <Grid>
              {isMultiBranch && branches.length > 1 && (
                <FormField label="Branch" className={FULL} hint="The branch this student belongs to. Also filters the batch list.">
                  <NativeSelect value={selectedBranch} onChange={(e) => handleBranchChange(e.target.value)}>
                    <option value="">Use default branch</option>
                    {branches.filter(b => b.isActive).map(b => (
                      <option key={b._id} value={b._id}>{b.name}{b.isDefault ? ' (Default)' : ''}</option>
                    ))}
                  </NativeSelect>
                </FormField>
              )}
              <FormField label="Course" htmlFor="sf-course" hint="You can assign course and batch later.">
                <NativeSelect id="sf-course" name="course" value={form.course} onChange={handleChange}>
                  <option value="">Select course</option>
                  {courseOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </NativeSelect>
              </FormField>
              <FormField label="Batch" htmlFor="sf-batch">
                <NativeSelect id="sf-batch" name="batch" value={form.batch} onChange={handleChange} disabled={!form.course}>
                  <option value="">Select batch</option>
                  {filteredBatchOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </NativeSelect>
              </FormField>
              <div className={FULL}>
                {form.course ? (
                  <CourseFeeFields
                    value={primaryFee}
                    onChange={setPrimaryFee}
                    listFee={listFeeOf(form.course)}
                    stored={primaryStoredNow}
                    courseName={courseLabel(form.course)}
                    serverError={serverErrorFor(form.course)}
                  />
                ) : (
                  <span className="tw-text-xs tw-text-content-subtle">Pick a course to fill in its fee and give this student a discount.</span>
                )}
              </div>
            </Grid>

            <Subsection
              icon={GraduationCap}
              title="Additional Courses"
              aside={(
                <AppButton variant="outline" size="sm" type="button" onClick={addAdditionalCourse}>
                  <Plus size={16} /> Add Course
                </AppButton>
              )}
            >
              {additionalCourses.length === 0 ? (
                <EmptyText>No additional courses. Click "Add Course" to assign more courses to this student.</EmptyText>
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-3">
                  {additionalCourses.map((ac, idx) => (
                    <div key={idx} className="tw-flex tw-items-start tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-p-3">
                      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3">
                        <div className="tw-grid tw-min-w-0 tw-gap-3 min-[768px]:tw-grid-cols-2">
                          <FormField label={`Additional course ${idx + 1}`} htmlFor={`sf-additional-course-${idx}`} labelTone="muted">
                            <NativeSelect id={`sf-additional-course-${idx}`} value={ac.courseId} onChange={(e) => updateAdditionalCourse(idx, 'courseId', e.target.value)}>
                              <option value="">Select course</option>
                              {additionalOptionsFor(idx).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </NativeSelect>
                          </FormField>
                          <FormField label="Batch" htmlFor={`sf-additional-batch-${idx}`} labelTone="muted">
                            <NativeSelect id={`sf-additional-batch-${idx}`} value={ac.batchId} onChange={(e) => updateAdditionalCourse(idx, 'batchId', e.target.value)} disabled={!ac.courseId}>
                              <option value="">Select batch</option>
                              {(additionalBatchOptions[idx] || []).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </NativeSelect>
                          </FormField>
                        </div>
                        {ac.courseId && (
                          <CourseFeeFields
                            value={ac.fee || EMPTY_FEE}
                            onChange={(fee) => updateAdditionalFee(idx, fee)}
                            listFee={listFeeOf(ac.courseId)}
                            stored={storedOf(ac)}
                            courseName={courseLabel(ac.courseId)}
                            serverError={serverErrorFor(ac.courseId)}
                          />
                        )}
                      </div>
                      <IconButton
                        label={ac.courseId ? `Remove ${courseLabel(ac.courseId)}` : "Remove this course"}
                        icon={Trash2}
                        variant="destructive"
                        size="sm"
                        type="button"
                        className="tw-mt-6 tw-shrink-0"
                        onClick={() => removeAdditionalCourse(idx)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Subsection>

            <Subsection
              icon={IndianRupee}
              title="Fees Overview"
              aside={<span className="tw-text-xs tw-text-content-subtle">Fees are collected from the Fees module</span>}
            >
              <div className="tw-grid tw-gap-3 min-[480px]:tw-grid-cols-3">
                <FeeTile label="Total Fees" value={formatCurrency(feesTotal)} />
                <FeeTile label="Paid" value={formatCurrency(feesPaid)} tone="paid" />
                <FeeTile label="Pending" value={formatCurrency(feesPending)} tone="pending" />
              </div>
              {(courseCount > 1 || feesDiscount > 0) && (
                <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-content-subtle">
                  {courseCount > 1 && feesDiscount > 0
                    ? `Across ${courseCount} courses, after ${formatCurrency(feesDiscount)} in discounts`
                    : courseCount > 1
                      ? `Across ${courseCount} courses`
                      : `After a ${formatCurrency(feesDiscount)} discount`}
                </p>
              )}
            </Subsection>
          </Section>

          {mode === "edit" && (
            <Section icon={Layers} title="Installment Plans">
              {courseAssignments.length === 0 ? (
                <EmptyText>No enrollments yet. Assign a course to set up an EMI plan.</EmptyText>
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-2.5">
                  {courseAssignments.map((ca) => {
                    const plan = planForCA(ca._id);
                    const caCourseId = String(ca.courseId?._id || ca.courseId || "");
                    const caName = ca.courseId?.name || courseOptions.find((o) => o.value === caCourseId)?.label || "Course";
                    // A plan's installments must add up to the fee; a new
                    // fee or discount does not rewrite a plan the institute chose.
                    const nextPayable = payableByCourse.get(caCourseId);
                    const planTotal = plan ? (plan.installments || []).reduce((s, i) => s + num(i.amount), 0) : null;
                    // ₹1, the drift the plan check itself allows for whole-rupee splits.
                    const planOff = plan && nextPayable !== undefined && Math.abs(planTotal - nextPayable) > 1;
                    return (
                      <div key={ca._id} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-px-3 tw-py-2.5">
                        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
                          <span className="tw-text-sm tw-font-semibold tw-text-content">{caName}</span>
                          <span className="tw-text-xs tw-text-content-muted">
                            {formatCurrency(num(ca.totalFees))} total
                            {plan ? ` · ${plan.installments?.length || 0} installments` : " · no plan"}
                          </span>
                          {planOff && (
                            <span className="tw-text-xs tw-text-warning-fg">
                              The plan totals {formatCurrency(planTotal)} but the fee will be {formatCurrency(nextPayable)}. Edit the plan after saving.
                            </span>
                          )}
                        </div>
                        <AppButton variant="outline" size="sm" type="button" onClick={() => setPlanModalCA(ca)}>
                          <CalendarClock size={16} /> {plan ? "Edit plan" : "Set up plan"}
                        </AppButton>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          )}

          <Section icon={ClipboardList} title="Other Details">
            <Grid>
              <fieldset className="tw-m-0 tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5 tw-border-0 tw-p-0">
                <legend className="tw-mb-1.5 tw-p-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content">Placement Required?</legend>
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  <Radio name="placement" value="Yes" label="Yes" variant="card" checked={form.placement === "Yes"} onChange={handleChange} />
                  <Radio name="placement" value="No" label="No" variant="card" checked={form.placement === "No"} onChange={handleChange} />
                </div>
              </fieldset>
              <FormField label="Status">
                <NativeSelect name="status" value={form.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </NativeSelect>
              </FormField>
              <FormField label="Reference Name">
                <Input name="refName" value={form.refName} onChange={handleChange} placeholder="Who referred this student?" />
              </FormField>
              <FormField label="Reference Number">
                <Input name="refNumber" value={form.refNumber} onChange={handleChange} placeholder="Reference contact" />
              </FormField>
              <FormField label="Comments" className={FULL}>
                <Textarea name="comments" value={form.comments} onChange={handleChange} rows={3} placeholder="Any additional notes about this student…" />
              </FormField>
            </Grid>
          </Section>
        </div>
      </div>

      <FormActionBar
        info={uploading && (
          <div className="tw-flex tw-min-w-[16rem] tw-flex-col tw-gap-1" role="status" aria-live="polite">
            <span className="tw-truncate">
              Uploading document {Math.min(uploading.done + 1, uploading.total)} of {uploading.total}
              {uploading.name ? ` — ${uploading.name}` : ""}
            </span>
            <ProgressBar value={uploading.percent} color="primary" />
          </div>
        )}
      >
        <AppButton variant="outline" type="button" onClick={onCancel || (() => navigate("/students"))} disabled={!!uploading}>
          Cancel
        </AppButton>
        <ResetButton onReset={handleReset} />
        <AppButton variant="default" type="submit" disabled={loading}>
          <Save size={16} />
          {loading ? (mode === "edit" ? "Saving…" : "Adding…") : (mode === "edit" ? "Save Changes" : "Add Student")}
        </AppButton>
      </FormActionBar>

      <FeePlanModal
        show={!!planModalCA}
        onHide={() => setPlanModalCA(null)}
        courseAssignment={planModalCA}
        existingPlan={planModalCA ? planForCA(planModalCA._id) : null}
        onSaved={() => { loadFeePlans(); setToast({ show: true, message: "Installment plan saved.", type: "success" }); }}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </form>
  );
};

export default StudentForm;
